import * as Haptics from 'expo-haptics';
import React from 'react';
import { Pressable, View, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ThemedText } from '../themed-text';
import { cn } from '@/utils/cn';

export interface FilterChipOption<T extends string> {
  label: string;
  value: T;
}

function selectionHaptic() {
  if (process.env.EXPO_OS === 'ios') {
    void Haptics.selectionAsync();
  }
}

export interface FilterPillProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Ocupa a coluna inteira da grade (rótulo centralizado). */
  fill?: boolean;
  /** Largura mínima da coluna, em % — usada pela grade. */
  basis?: string;
}

/**
 * Pílula de filtro — o único formato de filtro do app. Selecionada é sólida na
 * cor da marca; as outras são brancas com contorno fino, então a escolha atual
 * salta sem precisar de caixa nem faixa colorida em volta.
 */
export function FilterPill({ label, selected, onPress, fill, basis }: FilterPillProps) {
  return (
    <Pressable
      onPress={() => {
        selectionHaptic();
        onPress();
      }}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      className={cn(
        'rounded-full border px-3.5 py-2 active:opacity-80',
        selected ? 'border-primary bg-primary' : 'border-neutral-200 bg-card',
      )}
      style={
        {
          borderCurve: 'continuous',
          ...(fill ? { flexGrow: 1, flexBasis: basis ?? '30%' } : null),
        } as ViewStyle
      }
    >
      <ThemedText
        className={cn(
          'text-xs font-semibold',
          fill && 'text-center',
          selected ? 'text-white' : 'text-neutral-600',
        )}
        numberOfLines={1}
      >
        {label}
      </ThemedText>
    </Pressable>
  );
}

export interface FilterChipsProps<T extends string> {
  options: FilterChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/** Chips pill de filtro — seleção única, quebrando em linhas. */
export function FilterChips<T extends string>({
  options,
  value,
  onChange,
  className,
}: FilterChipsProps<T>) {
  return (
    <View className={cn('flex-row flex-wrap gap-2', className)}>
      {options.map((option) => (
        <FilterPill
          key={option.value}
          label={option.label}
          selected={value === option.value}
          onPress={() => onChange(option.value)}
        />
      ))}
    </View>
  );
}

export interface FilterBarProps<T extends string> {
  options: FilterChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Rótulo curto acima das pílulas (ex.: "Período"). */
  label?: string;
  /** Resumo do recorte ativo (ex.: "8 a 14 de ago · 3 agendamentos"). */
  hint?: string;
  hintIcon?: React.ComponentProps<typeof Ionicons>['name'];
  /** Colunas da grade. 3 cabe rótulo curto; 2 para rótulos longos. */
  columns?: 2 | 3;
  className?: string;
}

/**
 * Grade de filtro: todas as opções à vista, em colunas de largura igual — nada
 * de rolagem escondendo escolha nem de fileira torta. O recorte ativo é dito em
 * texto discreto embaixo, no lugar da antiga faixa colorida.
 */
export function FilterBar<T extends string>({
  options,
  value,
  onChange,
  label,
  hint,
  hintIcon = 'calendar-outline',
  columns = 3,
  className,
}: FilterBarProps<T>) {
  // Abaixo da divisão exata para o gap caber sem empurrar a última coluna.
  const basis = columns === 2 ? '46%' : '29%';

  return (
    <View className={cn('gap-2', className)}>
      {label ? (
        <ThemedText className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          {label}
        </ThemedText>
      ) : null}

      <View className="flex-row flex-wrap gap-2">
        {options.map((option) => (
          <FilterPill
            key={option.value}
            label={option.label}
            selected={value === option.value}
            onPress={() => onChange(option.value)}
            fill
            basis={basis}
          />
        ))}
      </View>

      {hint ? (
        <View className="flex-row items-center gap-1.5 pt-0.5">
          <Ionicons className="text-neutral-400" name={hintIcon} size={13} />
          <ThemedText className="text-xs text-neutral-500">{hint}</ThemedText>
        </View>
      ) : null}
    </View>
  );
}
